/**
 * Client twin of `backend/ptz.py`.
 *
 * Same split as `roiGeometry.ts` / `detection_roi.py` and `lineGeometry.ts` /
 * `tripwire_lines.py`: the geometry and the refusals are written twice so the browser
 * can preview a move without a round trip, and both copies are pinned against the same
 * table of cases (`ptz.test.ts` here, `test_ptz.py` there). **Change one and you must
 * change the other and both tables.**
 *
 * Two axes only. The VIGI C540-W has a fixed 4 mm lens and reports no zoom axis at all,
 * so there is no zoom here and there must never be one — a zoom control would be a dead
 * button. Reasoning and the measurements:
 * `docs/superpowers/specs/2026-09-01-camera-ptz-control-design.md`.
 */

/** One nudge may not cross half the travel. */
export const MAX_STEP = 0.5;
/** ~8.7 deg of pan, ~3.0 deg of tilt: about a tenth of the frame. */
export const DEFAULT_STEP = 0.05;
export const MIN_STEP = 0.001;

/**
 * MEASURED on a VIGI C540-W, 2026-09-01, by commanding a step and phase-correlating
 * the frames before and after:
 *
 *   pan  +0.049937 u -> -91.6 px of 848  => 0.4624 units per frame WIDTH  (HFOV 80.9 deg)
 *   tilt +0.05     u -> +34.7 px of 480  => 0.6924 units per frame HEIGHT (VFOV 41.5 deg)
 *
 * These are the measurement, not the datasheet — whose own three FOV figures are
 * mutually inconsistent, the lens being barrel-distorted with no published model.
 */
export const UNITS_PER_FRAME_WIDTH = 0.4624;
export const UNITS_PER_FRAME_HEIGHT = 0.6924;

export const REFUSAL = {
  disabled: 'Camera control is not enabled on this deployment',
  noAgent: 'No PTZ agent is configured for this deployment',
  noCamera: 'No steerable camera is bound to this feed',
  noFeed: 'Select a video source first',
  tooLarge: `A single step may not exceed ${MAX_STEP} of the travel`,
  tooSmall: `A step smaller than ${MIN_STEP} would not move the camera`,
  bad: 'Pan and tilt steps must be finite numbers',
} as const;

export type Direction = 'up' | 'down' | 'left' | 'right'
  | 'up-left' | 'up-right' | 'down-left' | 'down-right';

/** Why this nudge is unacceptable, or null. A string, never a bool: a silent no-op
 *  reads as a broken control, "camera is at its tilt limit" reads as a camera. */
export function validateStep(dx: number, dy: number): string | null {
  for (const v of [dx, dy]) {
    if (typeof v !== 'number' || !Number.isFinite(v)) return REFUSAL.bad;
  }
  if (Math.abs(dx) > MAX_STEP || Math.abs(dy) > MAX_STEP) return REFUSAL.tooLarge;
  if (Math.abs(dx) < MIN_STEP && Math.abs(dy) < MIN_STEP) return REFUSAL.tooSmall;
  return null;
}

/**
 * The (dx, dy) for one press of a direction pad.
 *
 * `+dy` tilts the camera UP — measured, the picture content moved DOWN — so 'up' is a
 * positive dy. Diagonals are NOT scaled by 1/sqrt(2): a pad press is a fixed step on
 * each axis, and normalising the magnitude would make a diagonal press move less far
 * horizontally than a horizontal one, which reads as the control sticking.
 */
export function nudgeFor(direction: Direction, step = DEFAULT_STEP): [number, number] {
  const up = direction.startsWith('up') ? 1 : direction.startsWith('down') ? -1 : 0;
  const right = direction.endsWith('right') ? 1 : direction.endsWith('left') ? -1 : 0;
  return [right * step, up * step];
}

const clampAxis = (v: number) => (v < -1 ? -1 : v > 1 ? 1 : v);

/**
 * The nudge that brings the normalised pixel (u, v) to the centre of frame.
 *
 * (0.5, 0.5) is the centre; u grows right and v grows DOWN, the image convention
 * `roiGeometry.toNormalized()` already produces. dy is negated against v because a
 * positive dy tilts the camera up, which moves content down.
 *
 * Linear and measured, not a tan projection from a focal length — over the middle of
 * the frame the measurement agreed with a tan model to ~1%, and at the edges this lens
 * distorts in a way no model here would capture. **Judge it on whether the thing
 * clicked landed near the middle.** A second click converges.
 */
export function pixelToOffset(u: number, v: number): [number, number] {
  return [
    clampAxis((u - 0.5) * UNITS_PER_FRAME_WIDTH),
    clampAxis((0.5 - v) * UNITS_PER_FRAME_HEIGHT),
  ];
}

/** Is this camera standing on a preset, i.e. are its detection area, counting lines
 *  and calibration currently describing what the lens sees? */
export function bindingsStale(
  presets: readonly unknown[] | undefined,
  activePresetId: number | null | undefined,
): boolean {
  return Boolean(presets && presets.length) && (activePresetId === null || activePresetId === undefined);
}
